import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, X, HelpCircle } from "lucide-react";
import { navigationService } from "@/services/navigationService";
import { CustomAvatarGroup } from "@/components/custom/CustomAvatarGroup";
import { RSVPButton } from "@/components/ui/rsvp-button";
import { RsvpStatusIcon } from "@/components/event-details/guest-view/components/RsvpStatusIcon";

const DesignSystem = () => {
  return (
    <div className="container mx-auto py-8 px-4 mb-24">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Design System</h1>
        <Button onClick={() => navigationService.goBack()}>Go Back</Button>
      </div>

      <Tabs defaultValue="components">
        <TabsList className="mb-8">
          <TabsTrigger value="colors">Colors</TabsTrigger>
          <TabsTrigger value="typography">Typography</TabsTrigger>
          <TabsTrigger value="components">Components</TabsTrigger>
          <TabsTrigger value="avatar">Avatar System</TabsTrigger>
          <TabsTrigger value="rsvp">RSVP Components</TabsTrigger>
        </TabsList>

        <TabsContent value="colors">
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Color Palette</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="h-20 bg-primary rounded-lg mb-2"></div>
                  <p className="text-sm font-medium">Primary</p>
                  <p className="text-xs text-gray-500">#000000</p>
                </div>
                <div>
                  <div className="h-20 bg-secondary rounded-lg mb-2"></div>
                  <p className="text-sm font-medium">Secondary</p>
                  <p className="text-xs text-gray-500">#F0F0F0</p>
                </div>
                <div>
                  <div className="h-20 bg-destructive rounded-lg mb-2"></div>
                  <p className="text-sm font-medium">Destructive</p>
                  <p className="text-xs text-gray-500">#E63946</p>
                </div>
                <div>
                  <div className="h-20 bg-muted rounded-lg mb-2"></div>
                  <p className="text-sm font-medium">Muted</p>
                  <p className="text-xs text-gray-500">#F9FAFB</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="typography">
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Typography</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h1 className="text-4xl font-bold">Heading 1</h1>
                  <p className="text-sm text-gray-500">text-4xl font-bold</p>
                </div>
                <div>
                  <h2 className="text-3xl font-semibold">Heading 2</h2>
                  <p className="text-sm text-gray-500">text-3xl font-semibold</p>
                </div>
                <div>
                  <h3 className="text-2xl font-medium">Heading 3</h3>
                  <p className="text-sm text-gray-500">text-2xl font-medium</p>
                </div>
                <div>
                  <h4 className="text-xl font-medium">Heading 4</h4>
                  <p className="text-sm text-gray-500">text-xl font-medium</p>
                </div>
                <div>
                  <p className="text-base">Body Text</p>
                  <p className="text-sm text-gray-500">text-base</p>
                </div>
                <div>
                  <p className="text-sm">Small Text</p>
                  <p className="text-sm text-gray-500">text-sm</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="components">
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Buttons</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  <Button variant="default">Default</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="destructive">Destructive</Button>
                  <Button variant="outline">Outline</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="link">Link</Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Badges</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  <Badge variant="default">Default</Badge>
                  <Badge variant="secondary">Secondary</Badge>
                  <Badge variant="destructive">Destructive</Badge>
                  <Badge variant="outline">Outline</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Form Elements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-w-sm space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input type="email" id="email" placeholder="Enter your email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="disabled">Disabled Input</Label>
                    <Input type="text" id="disabled" disabled placeholder="Disabled input" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cards</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold mb-2">Basic Card</h3>
                    <p className="text-gray-500">This is a basic card component with some content.</p>
                  </Card>
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold mb-2">Interactive Card</h3>
                    <p className="text-gray-500 mb-4">This card has a button to demonstrate interaction.</p>
                    <Button>Click me</Button>
                  </Card>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Separators</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="mb-2">Default Separator</p>
                    <Separator />
                  </div>
                  <div>
                    <p className="mb-2">Vertical Separator</p>
                    <div className="h-8 flex items-center">
                      <span>Left</span>
                      <Separator orientation="vertical" className="mx-4" />
                      <span>Right</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="avatar">
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Avatar System</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Individual Avatars</h3>
                  <div className="flex flex-wrap gap-6">
                    <div className="flex flex-col items-center gap-2">
                      <Avatar className="h-16 w-16 !bg-black !text-white !border-2 !border-white overflow-visible">
                        <AvatarFallback className="!bg-black !text-white font-medium">JD</AvatarFallback>
                      </Avatar>
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500" />
                        <span className="text-sm">Attending</span>
                      </div>
                    </div>

                    <div className="flex flex-col items-center gap-2">
                      <Avatar className="h-16 w-16 !bg-white !text-black !border-2 !border-black overflow-visible">
                        <AvatarFallback className="!bg-white !text-black font-medium">AB</AvatarFallback>
                      </Avatar>
                      <div className="flex items-center gap-2">
                        <HelpCircle className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">Yet to respond</span>
                      </div>
                    </div>

                    <div className="flex flex-col items-center gap-2">
                      <Avatar className="h-16 w-16 !bg-gray-200 !text-black !border-2 !border-black overflow-visible">
                        <AvatarFallback className="!bg-gray-200 !text-black font-medium">CD</AvatarFallback>
                      </Avatar>
                      <div className="flex items-center gap-2">
                        <X className="h-4 w-4 text-red-500" />
                        <span className="text-sm">Not attending</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Avatar Groups</h3>
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-base font-medium mb-2">Mixed Status Group</h4>
                      <CustomAvatarGroup 
                        users={[
                          { full_name: "John Doe", rsvp_status: "accepted" },
                          { full_name: "Alice Smith", rsvp_status: "invited" },
                          { full_name: "Bob Johnson", rsvp_status: "declined" },
                          { full_name: "Emma Wilson", rsvp_status: "invited" },
                          { full_name: "Mike Brown", rsvp_status: "accepted" },
                        ]}
                        maxVisible={4}
                        size="md"
                      />
                      <p className="text-sm text-gray-500 mt-2">Group with mixed RSVP statuses</p>
                    </div>
                    
                    <div>
                      <h4 className="text-base font-medium mb-2">All Attending Group</h4>
                      <CustomAvatarGroup 
                        users={[
                          { full_name: "John Doe", rsvp_status: "accepted" },
                          { full_name: "Alice Smith", rsvp_status: "accepted" },
                          { full_name: "Bob Johnson", rsvp_status: "accepted" },
                        ]}
                        maxVisible={3}
                        size="md"
                      />
                      <p className="text-sm text-gray-500 mt-2">Group with all attending</p>
                    </div>
                    
                    <div>
                      <h4 className="text-base font-medium mb-2">All Yet to Respond</h4>
                      <CustomAvatarGroup 
                        users={[
                          { full_name: "John Doe", rsvp_status: "invited" },
                          { full_name: "Alice Smith", rsvp_status: "invited" },
                          { full_name: "Bob Johnson", rsvp_status: "invited" },
                        ]}
                        maxVisible={3}
                        size="md"
                      />
                      <p className="text-sm text-gray-500 mt-2">Group with all yet to respond</p>
                    </div>
                    
                    <div>
                      <h4 className="text-base font-medium mb-2">All Not Attending</h4>
                      <CustomAvatarGroup 
                        users={[
                          { full_name: "John Doe", rsvp_status: "declined" },
                          { full_name: "Alice Smith", rsvp_status: "declined" },
                          { full_name: "Bob Johnson", rsvp_status: "declined" },
                        ]}
                        maxVisible={3}
                        size="md"
                      />
                      <p className="text-sm text-gray-500 mt-2">Group with all not attending</p>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Avatar Sizes</h3>
                  <div className="flex flex-wrap items-end gap-6">
                    <div className="flex flex-col items-center gap-2">
                      <Avatar className="h-8 w-8 !bg-white !text-black !border-2 !border-black overflow-visible">
                        <AvatarFallback className="!bg-white !text-black font-medium">SM</AvatarFallback>
                      </Avatar>
                      <span className="text-sm">Small</span>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <Avatar className="h-10 w-10 !bg-white !text-black !border-2 !border-black overflow-visible">
                        <AvatarFallback className="!bg-white !text-black font-medium">MD</AvatarFallback>
                      </Avatar>
                      <span className="text-sm">Medium</span>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <Avatar className="h-12 w-12 !bg-white !text-black !border-2 !border-black overflow-visible">
                        <AvatarFallback className="!bg-white !text-black font-medium">LG</AvatarFallback>
                      </Avatar>
                      <span className="text-sm">Large</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rsvp">
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>RSVP Button</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-6">
                  <div>
                    <h3 className="text-base font-medium mb-4">Default RSVP Button</h3>
                    <div className="flex justify-center mb-6">
                      <RSVPButton>RSVP to Event</RSVPButton>
                    </div>
                    <p className="text-sm text-muted-foreground">The primary call-to-action button used in the RSVP flow</p>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h3 className="text-base font-medium mb-4">Disabled RSVP Button</h3>
                    <div className="flex justify-center mb-6">
                      <RSVPButton disabled>Processing...</RSVPButton>
                    </div>
                    <p className="text-sm text-muted-foreground">Disabled state used during form submission</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>RSVP Status Indicators</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <RsvpStatusIcon status="accepted" />
                      <span className="font-medium">Accepted</span>
                    </div>
                    <p className="text-sm text-center text-muted-foreground">
                      Indicates a guest has accepted the invitation
                    </p>
                  </div>
                  
                  <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <RsvpStatusIcon status="declined" />
                      <span className="font-medium">Declined</span>
                    </div>
                    <p className="text-sm text-center text-muted-foreground">
                      Indicates a guest has declined the invitation
                    </p>
                  </div>
                  
                  <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <RsvpStatusIcon status="maybe" />
                      <span className="font-medium">Maybe</span>
                    </div>
                    <p className="text-sm text-center text-muted-foreground">
                      Indicates a guest has responded with "maybe"
                    </p>
                  </div>
                  
                  <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <RsvpStatusIcon status="invited" />
                      <span className="font-medium">Invited</span>
                    </div>
                    <p className="text-sm text-center text-muted-foreground">
                      Indicates a guest has been invited but hasn't responded
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>RSVP Interface Elements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-4">
                      <RsvpStatusIcon status="accepted" />
                      <span className="font-medium">You've RSVP'd: Attending</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline">Update My RSVP</Button>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-4">
                      <RsvpStatusIcon status="invited" />
                      <span className="font-medium">You haven't responded yet</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm">Respond to Invitation</Button>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-amber-600 mb-2">
                      RSVP updates are no longer available as the host is finalizing plans
                    </p>
                    <Button size="sm" disabled>Update My RSVP</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DesignSystem;
